import { PROVIDER_LIMITATIONS, RECEIPT_NAME, STANDARD, TRUST_BOUNDARY } from "./constants";
import { BUYER_FUNDING_PROCESS_BOUNDARY, flowEvidenceFor } from "./buyerFundingFlowSmoke";
import { buildBuyerPortalSubmissionPacket } from "./buyerPortal";
import { buildFeasibleSpecPackage } from "./feasiblePackage";
import { sha256Hex, stableJson } from "./hash";
import { maskAccount, maskTrace } from "./masking";
import { formatMoney } from "./money";
import { computeMatches, fieldCrossReference, inferOpenGaps, sourceRowsFor, statusLine } from "./matchEngine";
import type { BuyerFundingReview, ReceiptPackage, ReviewEvent, SourceRow } from "./types";

export async function buildReceiptPackage(review: BuyerFundingReview): Promise<ReceiptPackage> {
  const completeReview = normalizeReview(review);
  const receiptHash = await sha256Hex(stableJson(completeReview));
  const saveBackNote = buildSaveBackNote(completeReview);
  const receiptHtml = renderReceiptHtml(completeReview, receiptHash, saveBackNote);
  const feasibleSpecPackage = buildFeasibleSpecPackage(completeReview);
  const buyerPortalSubmissionPacket = buildBuyerPortalSubmissionPacket(completeReview);
  const packet = {
    review: completeReview,
    sourceManifest: completeReview.sourceRows,
    fieldCrossReference: fieldCrossReference(completeReview),
    eventLog: completeReview.events,
    saveBackNote,
    trustBoundary: TRUST_BOUNDARY,
    receiptHash,
    feasibleSpecPackage,
    buyerPortalSubmissionPacket
  };
  return {
    receiptId: completeReview.reviewNumber,
    receiptVersion: completeReview.receiptVersion,
    receiptHash,
    statusLine: statusLine(completeReview),
    receiptHtml,
    sourceManifest: completeReview.sourceRows,
    fieldCrossReference: fieldCrossReference(completeReview),
    eventLog: completeReview.events,
    saveBackNote,
    packetJson: JSON.stringify(packet, null, 2),
    feasibleSpecPackage,
    buyerPortalSubmissionPacket
  };
}

export function normalizeReview(review: BuyerFundingReview): BuyerFundingReview {
  const sourceRows = sourceRowsFor(review);
  const withSources = { ...review, sourceRows };
  const matches = computeMatches(withSources);
  const withMatches = { ...withSources, matches };
  const openGaps = inferOpenGaps(withMatches);
  const withGaps = {
    ...withMatches,
    openGaps,
    status:
      review.status === "Hold / conflicting instructions reported"
        ? review.status
        : review.signoff.signedAt
          ? "Recorded"
          : review.status,
    signedAt: review.signoff.signedAt,
    updatedAt: review.updatedAt || new Date().toISOString()
  };
  return {
    ...withGaps,
    events: normalizeEvents(withGaps)
  };
}

export function buildSaveBackNote(review: BuyerFundingReview): string {
  const expected = formatMoney(review.expectedFunds.amount || review.expectedAmount);
  const gaps = review.openGaps.length ? "Open gaps retained. " : "";
  const flow = flowEvidenceFor(review);
  const flowNote = flow
    ? ` Known entry ${flow.knownEntryPoint}. Buyer viewed ${flow.buyerViewedAt || "not recorded"}. Buyer acknowledgment ${flow.buyerAcknowledgedAt || "not recorded"}.`
    : "";
  const conflictNote = flow?.conflictReport
    ? ` Conflict source ${flow.conflictReport.source}; routing ending ${flow.conflictReport.attackerRoutingLast4}; account ending ${flow.conflictReport.attackerAccountLast4}.`
    : "";
  return `Buyer Funding Review Receipt recorded for file ${review.fileNumber}. Expected funds: ${expected} ${review.fundingPurpose}. Funding instruction ${review.fundingInstruction.versionLabel || "not recorded"} recorded. Buyer acknowledgment ${review.buyerAcknowledgment.type}. Source account evidence ${review.sourceAccountEvidence.evidenceType}. Inbound receipt ${review.inboundReceipt.amountReceived ? "recorded" : "not recorded"}. ${gaps}Office action: ${review.officeAction.action}.${flowNote}${conflictNote} Receipt ID ${review.reviewNumber} ${review.receiptVersion}. Saved to ${review.saveBackLocation || "not recorded"}.`;
}

export function renderReceiptHtml(review: BuyerFundingReview, receiptHash: string, saveBackNote: string): string {
  const crossReference = fieldCrossReference(review);
  const rows = review.matches.length ? review.matches : computeMatches(review);
  const gaps = review.openGaps.length ? review.openGaps : inferOpenGaps(review);
  const flow = flowEvidenceFor(review);
  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(RECEIPT_NAME)} ${escapeHtml(review.reviewNumber)}</title>
  <style>
    body{margin:0;padding:36px;color:#111;background:#fff;font:14px/1.45 Arial,sans-serif}
    main{max-width:980px;margin:0 auto}
    h1{font-size:28px;margin:0 0 8px}
    h2{font-size:17px;margin:26px 0 8px;border-bottom:1px solid #bbb;padding-bottom:6px}
    h3{font-size:14px;margin:18px 0 8px}
    .status{display:inline-block;border:2px solid #111;padding:7px 10px;font-weight:700}
    .notice{border-left:4px solid #111;background:#f4f4f4;padding:10px 12px}
    dl{display:grid;grid-template-columns:230px 1fr;gap:7px 14px}
    dt{font-weight:700}dd{margin:0}
    table{width:100%;border-collapse:collapse;margin:8px 0 16px}
    th,td{border:1px solid #d8d8d8;padding:7px;text-align:left;vertical-align:top}
    th{background:#f0f0f0}
    footer{margin-top:32px;border-top:2px solid #111;padding-top:12px;font-size:12px}
  </style>
</head>
<body>
<main>
  <header>
    <h1>${escapeHtml(RECEIPT_NAME)}</h1>
    <p class="status">${escapeHtml(statusLine(review))}</p>
    ${flow ? `<p class="notice">${escapeHtml(BUYER_FUNDING_PROCESS_BOUNDARY)}</p>` : ""}
    <p class="notice">${escapeHtml(TRUST_BOUNDARY)}</p>
    <p>${escapeHtml(STANDARD)}</p>
    <p>On ${escapeHtml(review.signoff.signedAt || review.updatedAt)}, ${escapeHtml(review.signoff.signerName || review.createdBy)} reviewed buyer funding for file ${escapeHtml(review.fileNumber)}. Expected funds were ${escapeHtml(formatMoney(review.expectedFunds.amount || review.expectedAmount))} for ${escapeHtml(review.fundingPurpose)}, due ${escapeHtml(review.expectedFunds.dueAt || "not recorded")}. Funding instructions version ${escapeHtml(review.fundingInstruction.versionLabel || "not recorded")} was recorded. Buyer acknowledgment was ${escapeHtml(review.buyerAcknowledgment.type)}. Source account evidence was ${escapeHtml(review.sourceAccountEvidence.evidenceType)}. Payment initiation evidence was ${escapeHtml(review.paymentInitiation.evidenceType)}. Inbound receipt was ${escapeHtml(review.inboundReceipt.amountReceived ? "recorded" : "not recorded")}. The office action was ${escapeHtml(review.officeAction.action)}.</p>
  </header>
  ${flow ? renderFlowSection(review, flow) : ""}
  <section>
    <h2>Review Sign-Off</h2>
    <dl>
      <dt>Signed by</dt><dd>${escapeHtml(review.signoff.signerName || "Not recorded")}</dd>
      <dt>Role</dt><dd>${escapeHtml(review.signoff.role || "Not recorded")}</dd>
      <dt>Office</dt><dd>${escapeHtml(review.signoff.office || review.officeName)}</dd>
      <dt>Authentication</dt><dd>${escapeHtml(review.signoff.authenticationMethod || "Cloudflare Access")}</dd>
      <dt>Signed at</dt><dd>${escapeHtml(review.signoff.signedAt || "Not recorded")}</dd>
      <dt>Funds status</dt><dd>${escapeHtml(review.signoff.fundsStatusAtSignoff || review.fundsStatus)}</dd>
      <dt>Gaps acknowledged</dt><dd>${escapeHtml(review.signoff.gapsAcknowledged ? "Yes" : "No")}</dd>
      <dt>Action at sign-off</dt><dd>${escapeHtml(review.signoff.officeAction || review.officeAction.action)}</dd>
      <dt>Receipt hash</dt><dd>${escapeHtml(receiptHash)}</dd>
    </dl>
  </section>
  <section>
    <h2>Expected Funds</h2>
    <dl>
      <dt>Amount</dt><dd>${escapeHtml(formatMoney(review.expectedFunds.amount || review.expectedAmount))}</dd>
      <dt>Purpose</dt><dd>${escapeHtml(review.expectedFunds.purpose)}</dd>
      <dt>Due date</dt><dd>${escapeHtml(review.expectedFunds.dueAt || "Not recorded")}</dd>
      <dt>Source document</dt><dd>${escapeHtml(review.expectedFunds.sourceDocumentId || "Not recorded")}</dd>
      <dt>Source status</dt><dd>${escapeHtml(review.expectedFunds.sourceStatus)}</dd>
      <dt>Prior amount comparison</dt><dd>${escapeHtml(review.expectedFunds.priorAmount ? formatMoney(review.expectedFunds.priorAmount) : "Not recorded")}</dd>
    </dl>
  </section>
  <section>
    <h2>Funding Instructions</h2>
    <dl>
      <dt>Instruction version</dt><dd>${escapeHtml(review.fundingInstruction.versionLabel || "Not recorded")}</dd>
      <dt>Sent timestamp</dt><dd>${escapeHtml(review.fundingInstruction.sentAt || "Not recorded")}</dd>
      <dt>Sent channel</dt><dd>${escapeHtml(review.fundingInstruction.deliveryChannel)}</dd>
      <dt>Payment rails allowed</dt><dd>${escapeHtml(review.fundingInstruction.allowedRails.join(", "))}</dd>
      <dt>Receiving account summary</dt><dd>${escapeHtml(maskAccount(review.fundingInstruction.receivingAccountLast4))}</dd>
      <dt>Buyer acknowledgment status</dt><dd>${escapeHtml(review.buyerAcknowledgment.type)}</dd>
    </dl>
  </section>
  <section>
    <h2>Source / Payment Evidence</h2>
    <dl>
      <dt>Source party</dt><dd>${escapeHtml(review.sourceAccountEvidence.sourceParty || review.fundingSource.fundingPartyName || "Not recorded")}</dd>
      <dt>Account evidence</dt><dd>${escapeHtml(review.sourceAccountEvidence.evidenceType)}</dd>
      <dt>Payment rail</dt><dd>${escapeHtml(review.paymentRail.rail)}</dd>
      <dt>Initiation evidence</dt><dd>${escapeHtml(review.paymentInitiation.evidenceType)}</dd>
      <dt>Trace/reference</dt><dd>${escapeHtml(maskTrace(review.paymentInitiation.traceNumber || review.paymentInitiation.reference))}</dd>
      <dt>Limitations</dt><dd>${escapeHtml(review.sourceAccountEvidence.limitation)}</dd>
    </dl>
  </section>
  <section>
    <h2>Inbound Match</h2>
    <dl>
      <dt>Amount received</dt><dd>${escapeHtml(formatMoney(review.inboundReceipt.amountReceived))}</dd>
      <dt>Received timestamp</dt><dd>${escapeHtml(review.inboundReceipt.receivedAt || "Not recorded")}</dd>
      <dt>Sender shown</dt><dd>${escapeHtml(review.inboundReceipt.senderShown || "Not recorded")}</dd>
      <dt>Receiving account</dt><dd>${escapeHtml(maskAccount(review.inboundReceipt.receivingAccountLast4))}</dd>
      <dt>Reference / memo</dt><dd>${escapeHtml(review.inboundReceipt.reference || review.inboundReceipt.memo || "Not recorded")}</dd>
      <dt>Ledger entry</dt><dd>${escapeHtml(review.inboundReceipt.trustLedgerEntryId || "Not recorded")}</dd>
      <dt>Match result</dt><dd>${escapeHtml(statusLine(review))}</dd>
    </dl>
  </section>
  <section><h2>What Matched</h2>${table(["Row", "Result", "Expected", "Observed", "Source rows"], rows.map((row) => [row.label, row.result, row.expectedValue || "", row.observedValue || "", row.sourceRowIds.join(", ")]))}</section>
  <section><h2>What Stayed Open</h2>${gaps.length ? table(["Gap", "Severity", "Status", "Why it matters", "Source rows"], gaps.map((gap) => [gap.gapType, gap.severity, gap.status, gap.description, gap.sourceRowIds.join(", ")])) : "<p>No open gaps recorded.</p>"}</section>
  <section>
    <h2>What The Office Did</h2>
    <dl>
      <dt>Office action</dt><dd>${escapeHtml(review.officeAction.action)}</dd>
      <dt>Notes</dt><dd>${escapeHtml(review.officeAction.note || "Not recorded")}</dd>
      <dt>Ledger reference</dt><dd>${escapeHtml(review.officeAction.ledgerReference || review.inboundReceipt.trustLedgerEntryId || "Not recorded")}</dd>
      <dt>Manager / owner exception</dt><dd>${escapeHtml(review.officeAction.managerReviewStatus || "Not recorded")}</dd>
    </dl>
  </section>
  <section><h2>Appendix A - Evidence Source Rows</h2>${table(["Source row ID", "Source type", "Source file", "Hash", "Linked receipt claims", "Limitation"], review.sourceRows.map((source) => [source.id, source.sourceType, source.title, source.sourceHash || "", source.claimIds.join(", "), source.limitation]))}</section>
  <section><h2>Appendix B - Field Cross-Reference</h2>${table(["Field", "Source", "Evidence", "Result"], crossReference.map((row) => [row.field, row.source, row.evidence, row.result]))}</section>
  <section><h2>Appendix C - Event Log</h2>${table(["Timestamp", "Actor", "Event type", "Source rows"], review.events.map((event) => [event.occurredAt, event.actorId, event.eventType, event.sourceRowIds.join(", ")]))}</section>
  <section><h2>Appendix D - Provider Limitations</h2>${table(["Provider", "Limitation"], PROVIDER_LIMITATIONS.map((row) => [row.provider, row.limitation]))}</section>
  ${renderFeasibleAppendix(review)}
  <section><h2>Save-Back Note</h2><p>${escapeHtml(saveBackNote)}</p></section>
  <footer>
    <p>Receipt ID ${escapeHtml(review.reviewNumber)} · Version ${escapeHtml(review.receiptVersion)} · Supersedes ${escapeHtml(review.supersedesReviewId || "none")} · Save-back location ${escapeHtml(review.saveBackLocation || "not recorded")} · Hash ${escapeHtml(receiptHash)}</p>
    <p>${escapeHtml(TRUST_BOUNDARY)}</p>
  </footer>
</main>
</body>
</html>`;
}

function table(headers: string[], rows: string[][]): string {
  return `<table><thead><tr>${headers.map((header) => `<th>${escapeHtml(header)}</th>`).join("")}</tr></thead><tbody>${rows
    .map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell || "Not recorded")}</td>`).join("")}</tr>`)
    .join("")}</tbody></table>`;
}

function renderFeasibleAppendix(review: BuyerFundingReview): string {
  const feasible = buildFeasibleSpecPackage(review);
  const buyerPacket = buildBuyerPortalSubmissionPacket(review);
  const gateRows = feasible.policyGates.map((gate) => [
    gate.name,
    gate.status,
    gate.note,
    gate.retainedGapIds.length ? gate.retainedGapIds.join(", ") : "None"
  ]);
  const integrationRows = feasible.integrationStatus.map((item) => [item.name, item.mode, item.reason]);
  return `<section>
    <h2>Appendix E - Feasible Spec Coverage</h2>
    <p>Policy gates, buyer collection posture, accounting reconciliation, and integration status for the current build.</p>
    <h3>Policy Gates</h3>
    ${table(["Gate", "Status", "Note", "Retained gap IDs"], gateRows)}
    <h3>Buyer Portal</h3>
    <dl>
      <dt>Submissions</dt><dd>${buyerPacket.submittedCount}</dd>
      <dt>Portal status</dt><dd>${escapeHtml(buyerPacket.status)}</dd>
    </dl>
    <h3>Accounting Reconciliation</h3>
    <dl>
      <dt>Expected</dt><dd>${escapeHtml(feasible.accountingReconciliation.amountExpected)}</dd>
      <dt>Received</dt><dd>${escapeHtml(feasible.accountingReconciliation.amountReceived)}</dd>
      <dt>Difference</dt><dd>${escapeHtml(feasible.accountingReconciliation.difference)}</dd>
      <dt>Recommended state</dt><dd>${escapeHtml(feasible.accountingReconciliation.recommendedAccountingState)}</dd>
    </dl>
    <h3>Integration Status</h3>
    ${table(["Integration", "Mode", "Reason"], integrationRows)}
  </section>`;
}

function renderFlowSection(
  review: BuyerFundingReview,
  flow: ReturnType<typeof flowEvidenceFor>
): string {
  if (!flow) return "";
  const conflict = flow.conflictReport;
  return `<section>
    <h2>Buyer Funding Instruction Process</h2>
    <dl>
      <dt>File</dt><dd>${escapeHtml(review.fileNumber)}</dd>
      <dt>Office</dt><dd>${escapeHtml(review.officeName)}</dd>
      <dt>Buyer</dt><dd>${escapeHtml(review.buyerParties[0]?.name || "Not recorded")}</dd>
      <dt>Property</dt><dd>${escapeHtml(review.propertyAddress || "Not recorded")}</dd>
      <dt>Amount due</dt><dd>${escapeHtml(formatMoney(review.expectedFunds.amount || review.expectedAmount))}</dd>
      <dt>Known Veto entry point</dt><dd>${escapeHtml(flow.knownEntryPoint)}</dd>
      <dt>Instruction version</dt><dd>${escapeHtml(review.fundingInstruction.versionLabel || "Not recorded")}</dd>
      <dt>Beneficiary</dt><dd>${escapeHtml(review.fundingInstruction.beneficiaryName || "Not recorded")}</dd>
      <dt>Bank</dt><dd>${escapeHtml(review.fundingInstruction.receivingBankName || "Not recorded")}</dd>
      <dt>Routing last four</dt><dd>${escapeHtml(review.fundingInstruction.routingLast4 || "Not recorded")}</dd>
      <dt>Destination account last four</dt><dd>${escapeHtml(review.fundingInstruction.receivingAccountLast4 || "Not recorded")}</dd>
      <dt>Source account last four</dt><dd>${escapeHtml(review.sourceAccountEvidence.accountLast4 || "Not recorded")}</dd>
      <dt>Office published timestamp</dt><dd>${escapeHtml(flow.officePublishedAt)}</dd>
      <dt>Buyer viewed timestamp</dt><dd>${escapeHtml(flow.buyerViewedAt || "Not recorded")}</dd>
      <dt>Buyer acknowledgment statement</dt><dd>${escapeHtml(flow.acknowledgmentStatement || "Not recorded")}</dd>
      <dt>Buyer acknowledged timestamp</dt><dd>${escapeHtml(flow.buyerAcknowledgedAt || review.buyerAcknowledgment.acknowledgedAt || "Not recorded")}</dd>
      ${
        flow.docuSignEnvelopeId
          ? `<dt>Buyer acknowledgment — Signed</dt><dd>DocuSign envelope ${escapeHtml(flow.docuSignEnvelopeId)} · instruction ${escapeHtml(review.buyerAcknowledgment.acknowledgedInstructionVersion || "Not recorded")}</dd>
      <dt>DocuSign signed at</dt><dd>${escapeHtml(flow.docuSignSignedAt || "Not recorded")}</dd>
      <dt>Signer email</dt><dd>${escapeHtml(flow.docuSignSignerEmail || "Not recorded")}</dd>
      <dt>Instruction hash</dt><dd>${escapeHtml(flow.docuSignInstructionHash || "Not recorded")}</dd>
      <dt>Document hash</dt><dd>${escapeHtml(flow.docuSignDocumentHash || "Not recorded")}</dd>
      <dt>Acknowledgment limitation</dt><dd>${escapeHtml(review.buyerAcknowledgment.limitation)}</dd>`
          : flow.instructionAckMethod
            ? `<dt>Instruction acknowledgment method</dt><dd>${escapeHtml(flow.instructionAckMethod)}${flow.instructionAckCredentialId ? ` · credential ${escapeHtml(flow.instructionAckCredentialId)}` : ""}</dd>`
            : ""
      }
      ${
        flow.addressValidationAt
          ? `<dt>Address validation</dt><dd>${escapeHtml(flow.addressValidationVerdict || "Recorded")} · ${escapeHtml(flow.addressValidationAt)}${flow.addressValidationPlaceId ? ` · place ${escapeHtml(flow.addressValidationPlaceId)}` : ""}</dd>`
          : ""
      }
      <dt>Conflicting-instructions report</dt><dd>${escapeHtml(conflict ? "conflicting_instruction_report" : "Not recorded")}</dd>
      <dt>Conflict source</dt><dd>${escapeHtml(conflict?.source || "Not recorded")}</dd>
      <dt>Attacker routing last four</dt><dd>${escapeHtml(conflict?.attackerRoutingLast4 || "Not recorded")}</dd>
      <dt>Attacker account last four</dt><dd>${escapeHtml(conflict?.attackerAccountLast4 || "Not recorded")}</dd>
      <dt>Conflict reported timestamp</dt><dd>${escapeHtml(conflict?.reportedAt || "Not recorded")}</dd>
      <dt>Office action</dt><dd>${escapeHtml(review.officeAction.action)}</dd>
    </dl>
  </section>`;
}

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

const DEFAULT_EVENT_TYPES = [
  "Review created",
  "Expected funds recorded",
  "Funding instruction recorded",
  "Buyer acknowledgment recorded",
  "Source account evidence recorded",
  "Payment initiation evidence recorded",
  "Inbound receipt recorded",
  "Four-way match generated",
  "Open gaps recorded",
  "Office action recorded",
  "Reviewer signed",
  "Receipt exported"
] as const;

function normalizeEvents(review: BuyerFundingReview): ReviewEvent[] {
  const baseEvents =
    review.events.length > 0
      ? review.events
      : DEFAULT_EVENT_TYPES.map((eventType, index) => ({
          id: `${review.id}-event-${index + 1}`,
          reviewId: review.id,
          actorId: review.createdBy,
          eventType,
          occurredAt: new Date(new Date(review.createdAt).getTime() + index * 60000).toISOString(),
          objectType: "Buyer Funding Review",
          sourceRowIds: []
        }));

  return baseEvents.map((event, index) => {
    const fallbackIds = sourceRowIdsForEvent(event.eventType, review);
    return {
      ...event,
      id: event.id || `${review.id}-event-${index + 1}`,
      reviewId: event.reviewId || review.id,
      actorId: event.actorId || review.createdBy,
      occurredAt: event.occurredAt || review.updatedAt || new Date().toISOString(),
      sourceRowIds: event.sourceRowIds?.length ? stableSourceIds(event.sourceRowIds) : fallbackIds
    };
  });
}

function sourceRowIdsForEvent(eventType: string, review: BuyerFundingReview): string[] {
  const byType = sourceRowsByType(review.sourceRows);
  const lower = eventType.toLowerCase();
  if (lower.includes("expected")) return pick(byType.expected_funds);
  if (lower.includes("published") || lower.includes("publication")) {
    return pick(byType.instruction_publication, byType.funding_instruction);
  }
  if (lower.includes("conflicting") || lower.includes("conflict")) {
    return pick(byType.conflicting_instruction_report, byType.office_action);
  }
  if (lower.includes("known") || lower.includes("viewed") || lower.includes("entry")) {
    return pick(byType.buyer_entry);
  }
  if (lower.includes("instruction")) return pick(byType.funding_instruction);
  if (lower.includes("acknowledgment")) return pick(byType.buyer_acknowledgment_esign, byType.buyer_acknowledgment);
  if (lower.includes("source account")) return pick(byType.source_account_evidence);
  if (lower.includes("initiation")) return pick(byType.payment_initiation);
  if (lower.includes("inbound")) return pick(byType.inbound_receipt);
  if (lower.includes("match")) {
    return stableSourceIds(review.matches.flatMap((match) => match.sourceRowIds));
  }
  if (lower.includes("gap")) {
    const gapIds = review.openGaps.flatMap((gap) => gap.sourceRowIds);
    return gapIds.length ? stableSourceIds(gapIds) : pick(byType.office_action);
  }
  if (lower.includes("office action")) return pick(byType.office_action);
  if (lower.includes("signed") || lower.includes("sign")) return pick(byType.signoff);
  if (lower.includes("export") || lower.includes("save")) return pick(byType.export);
  return pick(byType.expected_funds, byType.funding_instruction, byType.inbound_receipt);
}

function sourceRowsByType(sourceRows: SourceRow[]): Record<string, string | undefined> {
  return Object.fromEntries(sourceRows.map((source) => [source.sourceType, source.id]));
}

function pick(...ids: Array<string | undefined>): string[] {
  return stableSourceIds(ids.filter((id): id is string => Boolean(id)));
}

function stableSourceIds(ids: string[]): string[] {
  return Array.from(new Set(ids)).sort();
}
